import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { Company, Trade } from '@types'

import { useModal } from '@hooks/app/modal.hook'

import { Modal } from '@components/Modal'
import { Input } from '@components/Input'
import { Refresh } from '@components/Refresh'

import { SelectCompany } from '@modules/_shared/components/SelectCompany'
import { SelectTrade } from '@modules/_shared/components/SelectTrade'
import { SelectTaskLibrary } from '@modules/project-library/components/SelectTaskLibrary'

import { useUpdateTaskTemplate } from '@hooks/task-template/update-task-template.hook'
import { useTaskTemplate } from '@hooks/task-template/task-template.hook'

import { Container } from './edit-task-template.styled'

export const EditTaskTemplatePage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const taskId = searchParams.get('taskId')

  const { projectId } = useParams()

  const { data, loading, error, refetch } = useTaskTemplate(taskId || '')

  const isLoading = loading ? 1 : error ? -1 : 0

  const task = data?.taskTemplate

  const [name, setName] = useState<string>('')
  const [trade, setTrade] = useState<Trade | undefined>()
  const [company, setCompany] = useState<Company | undefined>()
  const [duration, setDuration] = useState<number | undefined>(undefined)
  const [workforce, setWorkforce] = useState<number | undefined>(undefined)

  const { updateTaskTemplate } = useUpdateTaskTemplate()

  useEffect(() => {
    if (task) {
      setName(task.name)
      setTrade(task.trade)
      setCompany(task.company)
      setDuration(task.duration)
      setWorkforce(task.workforce)
    }
  }, [task])

  return (
    <Modal
      title={t('modules_project-settings_pages_edit_task_template_title')}
      onSubmitLabel={t('modules_project-settings_pages_edit_task_template_button_edit-task')}
      onClose={() => {
        hideModal('update_task_template', { taskId })
      }}
      onSubmit={() => {
        if (taskId && trade && duration != undefined && workforce != undefined) {
          updateTaskTemplate({
            id: taskId,
            name,
            duration,
            workforce,
            tradeKey: trade?.key,
            companyId: company?.id,
            description: '',
          })

          hideModal('update_task_template', { taskId })
        }
      }}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(task?.id || '')}>
          {task && (
            <>
              <SelectTaskLibrary
                autoFocus
                required
                label={t('modules_project-settings_pages_edit_task_template_label_task-name')}
                placeholder={t('modules_project-settings_pages_edit_task_template_placeholder_task-name')}
                taskName={name}
                onChangeTask={(value, taskLibrary) => {
                  setName(value)

                  setDuration(taskLibrary?.duration ?? duration)
                  setWorkforce(taskLibrary?.workforce ?? workforce)
                }}
              />

              <SelectTrade required trade={trade} onSelectTrade={setTrade} />

              <SelectCompany projectId={projectId || ''} company={company} onSelectCompany={setCompany} />

              <Input
                type="number"
                step={0.25}
                min={0.25}
                required
                label={t('modules_project-settings_pages_edit_task_template_label_duration')}
                placeholder={t('modules_project-settings_pages_edit_task_template_placeholder_duration')}
                value={`${duration}`}
                onChange={(v) => setDuration(parseFloat(v))}
              />
              <Input
                type="number"
                step={0.25}
                min={0}
                required
                label={t('modules_project-settings_pages_edit_task_template_label_workforce')}
                placeholder={t('modules_project-settings_pages_edit_task_template_placeholder_workforce')}
                value={`${workforce}`}
                onChange={(v) => setWorkforce(parseFloat(v))}
              />
            </>
          )}
        </Refresh>
      </Container>
    </Modal>
  )
}
